<template>
  <div>
    <!-- CHAT PANEL -->
    <div
      v-show="chatOpen"
      class="ps-chat"
      role="dialog"
      aria-label="PosSmart chat"
    >
      <div class="ps-chat__head">
        <span class="ps-chat__avatar">
          <img
            src="/images/logo_white.png"
            alt="PosSmart"
            class="ps-chat__avatar-img"
          />
          <span class="ps-chat__dot"></span>
        </span>
        <div class="ps-chat__head-text">
          <p class="ps-chat__title">{{ $t("chat.title") }}</p>
          <p class="ps-chat__status">{{ $t("chat.status") }}</p>
        </div>
        <button
          class="ps-chat__close"
          :aria-label="$t('chat.close')"
          @click="setChatOpen(false)"
        >
          <v-icon size="18">fal fa-times</v-icon>
        </button>
      </div>

      <div ref="msgBox" class="ps-chat__msgs thin-scroll">
        <template v-for="(m, i) in messages">
          <!-- message bubble -->
          <div
            v-if="m.type === 'msg'"
            :key="'msg' + i"
            class="ps-chat__bubble"
            :class="
              m.who === 'user'
                ? 'ps-chat__bubble--user'
                : 'ps-chat__bubble--bot'
            "
          >
            {{ m.text }}
          </div>

          <!-- welcome message (reactive to locale) -->
          <div
            v-else-if="m.type === 'welcome'"
            :key="'w' + i"
            class="ps-chat__bubble ps-chat__bubble--bot"
          >
            {{ $t("chat.welcome") }}
          </div>

          <!-- quick question chips -->
          <div
            v-else-if="m.type === 'chips'"
            :key="'c' + i"
            class="ps-chat__chips"
          >
            <button
              v-for="q in QUICK"
              :key="q"
              class="ps-chat__chip"
              @click="onQuick($t(q))"
            >
              {{ $t(q) }}
            </button>
          </div>

          <!-- survey option chips -->
          <div
            v-else-if="m.type === 'options'"
            :key="'o' + i"
            class="ps-chat__chips"
          >
            <template v-for="opt in surveySteps[m.step].options">
              <button
                v-if="surveySteps[m.step].type === 'single'"
                :key="opt"
                class="ps-chat__chip"
                @click="pickSingle(m.step, opt)"
              >
                {{ $t(opt) }}
              </button>
              <button
                v-else
                :key="opt"
                class="ps-chat__chip"
                :class="{
                  'ps-chat__chip--active': survey.multiSel.includes($t(opt)),
                }"
                @click="toggleMulti(m.step, opt)"
              >
                {{ $t(opt) }}
              </button>
            </template>
            <button
              v-if="surveySteps[m.step].type === 'multi'"
              class="ps-chat__chip ps-chat__chip--solid"
              @click="nextMulti(m.step)"
            >
              {{ $t("chat.next") }}
            </button>
          </div>

          <!-- survey finish actions -->
          <div
            v-else-if="m.type === 'actions'"
            :key="'a' + i"
            class="ps-chat__chips"
          >
            <a
              :href="m.waHref"
              target="_blank"
              rel="noopener"
              class="ps-chat__chip ps-chat__chip--whatsapp"
              >{{ $t("chat.whatsapp") }}</a
            >
            <button class="ps-chat__chip" @click="startSurvey">
              {{ $t("chat.again") }}
            </button>
          </div>
        </template>

        <!-- typing indicator -->
        <div
          v-if="typing"
          class="ps-chat__bubble ps-chat__bubble--bot ps-chat__typing"
        >
          <span></span><span></span><span></span>
        </div>
      </div>

      <div class="ps-chat__input-bar">
        <form class="ps-chat__form" @submit.prevent="sendChat(input)">
          <input
            ref="chatInputRef"
            v-model="input"
            type="text"
            autocomplete="off"
            :placeholder="$t(inputPlaceholder)"
            class="ps-chat__input"
          />
          <button type="submit" class="ps-chat__send">
            {{ $t("chat.send") }}
          </button>
        </form>
      </div>
    </div>

    <!-- FAB -->
    <button
      class="ps-chat__fab"
      :aria-label="$t('chat.open')"
      @click="setChatOpen(!chatOpen)"
    >
      <v-icon v-show="!chatOpen" size="26">fal fa-comment-dots</v-icon>
      <v-icon v-show="chatOpen" size="22">fal fa-times</v-icon>
      <span class="ps-chat__fab-dot"></span>
    </button>
  </div>
</template>

<script>
export default {
  name: "PsChatWidget",
  data() {
    return {
      chatOpen: false,
      input: "",
      inputPlaceholder: "chat.placeholder",
      typing: false,
      messages: [],
      // survey state
      survey: { active: false, step: 0, answers: {}, multiSel: [] },
      QUICK: ["chat.quick.0", "chat.quick.1", "chat.quick.2", "chat.quick.3"],
      pkgKeys: ["chat.pkg.0", "chat.pkg.1", "chat.pkg.2", "chat.pkg.3"],
      surveyLabels: {
        bizType: "chat.labels.bizType",
        productCount: "chat.labels.productCount",
        staffCount: "chat.labels.staffCount",
        branches: "chat.labels.branches",
        usage: "chat.labels.usage",
        equipment: "chat.labels.equipment",
        billing: "chat.labels.billing",
        posExp: "chat.labels.posExp",
        recording: "chat.labels.recording",
        painPoints: "chat.labels.painPoints",
        contactName: "chat.labels.contactName",
        storeName: "chat.labels.storeName",
        phone: "chat.labels.phone",
        email: "chat.labels.email",
        address: "chat.labels.address",
        location: "chat.labels.location",
      },
      surveySteps: [
        {
          key: "bizType",
          q: "chat.steps.0.q",
          type: "single",
          options: [
            "chat.steps.0.options.0",
            "chat.steps.0.options.1",
            "chat.steps.0.options.2",
            "chat.steps.0.options.3",
            "chat.steps.0.options.4",
            "chat.steps.0.options.5",
          ],
        },
        {
          key: "productCount",
          q: "chat.steps.1.q",
          type: "single",
          note: "chat.steps.1.note",
          options: [
            "chat.steps.1.options.0",
            "chat.steps.1.options.1",
            "chat.steps.1.options.2",
            "chat.steps.1.options.3",
          ],
        },
        {
          key: "staffCount",
          q: "chat.steps.2.q",
          type: "single",
          options: [
            "chat.steps.2.options.0",
            "chat.steps.2.options.1",
            "chat.steps.2.options.2",
            "chat.steps.2.options.3",
          ],
        },
        {
          key: "branches",
          q: "chat.steps.3.q",
          type: "single",
          options: [
            "chat.steps.3.options.0",
            "chat.steps.3.options.1",
            "chat.steps.3.options.2",
            "chat.steps.3.options.3",
          ],
        },
        {
          key: "usage",
          q: "chat.steps.4.q",
          type: "multi",
          options: [
            "chat.steps.4.options.0",
            "chat.steps.4.options.1",
            "chat.steps.4.options.2",
            "chat.steps.4.options.3",
          ],
        },
        {
          key: "equipment",
          q: "chat.steps.5.q",
          type: "multi",
          options: [
            "chat.steps.5.options.0",
            "chat.steps.5.options.1",
            "chat.steps.5.options.2",
            "chat.steps.5.options.3",
            "chat.steps.5.options.4",
            "chat.steps.5.options.5",
          ],
        },
        {
          key: "billing",
          q: "chat.steps.6.q",
          type: "single",
          note: "chat.steps.6.note",
          options: [
            "chat.steps.6.options.0",
            "chat.steps.6.options.1",
            "chat.steps.6.options.2",
            "chat.steps.6.options.3",
            "chat.steps.6.options.4",
          ],
        },
        {
          key: "posExp",
          q: "chat.steps.7.q",
          type: "single",
          options: [
            "chat.steps.7.options.0",
            "chat.steps.7.options.1",
            "chat.steps.7.options.2",
            "chat.steps.7.options.3",
          ],
        },
        {
          key: "recording",
          q: "chat.steps.8.q",
          type: "single",
          options: [
            "chat.steps.8.options.0",
            "chat.steps.8.options.1",
            "chat.steps.8.options.2",
            "chat.steps.8.options.3",
          ],
        },
        {
          key: "painPoints",
          q: "chat.steps.9.q",
          type: "multi",
          options: [
            "chat.steps.9.options.0",
            "chat.steps.9.options.1",
            "chat.steps.9.options.2",
            "chat.steps.9.options.3",
            "chat.steps.9.options.4",
            "chat.steps.9.options.5",
          ],
        },
        { key: "contactName", q: "chat.steps.10.q", type: "text" },
        { key: "storeName", q: "chat.steps.11.q", type: "text" },
        { key: "phone", q: "chat.steps.12.q", type: "text" },
        { key: "email", q: "chat.steps.13.q", type: "text" },
        { key: "address", q: "chat.steps.14.q", type: "text" },
        { key: "location", q: "chat.steps.15.q", type: "text" },
      ],
    };
  },
  mounted() {
    this.initWelcome();
  },
  methods: {
    scrollBottom() {
      this.$nextTick(() => {
        if (this.$refs.msgBox) {
          this.$refs.msgBox.scrollTop = this.$refs.msgBox.scrollHeight;
        }
      });
    },
    addMsg(text, who) {
      this.messages.push({ type: "msg", who, text });
      this.scrollBottom();
    },
    setChatOpen(open) {
      this.chatOpen = open;
      if (open) {
        this.$nextTick(() => {
          if (this.$refs.chatInputRef) this.$refs.chatInputRef.focus();
        });
      }
    },
    initWelcome() {
      this.messages = [];
      this.messages.push({ type: "welcome" });
      this.messages.push({ type: "chips" });
      this.scrollBottom();
    },
    // ===== demo AI replies =====
    demoReply(msg) {
      const m = msg.toLowerCase();
      if (
        /(ລາຄາ|ເພັດເກັດ|ກີ່ປາຍ|ເທົ່າໃດ|price|cost|ປະຢັດ|ราคา|价格|多少钱|เท่าไร)/.test(
          m,
        )
      )
        return this.$t("chat.replyPrice");
      if (
        /(ລົງທະບຽນ|ສະໝັກ|ເລີ່ມ|start|register|ຟຣີ|trial|14|สมัคร|注册|开始|免费)/.test(
          m,
        )
      )
        return this.$t("chat.replyRegister");
      if (
        /(ຕິດຕໍ່|help|ຊ່ວຍ|ບັນຫາ|contact|support|ທີມ|ติดต่อ|帮助|联系)/.test(m)
      )
        return this.$t("chat.replyContact");
      if (/(ສາຂາ|ຫຼາຍສາຂາ|branch|store|สาขา|分店)/.test(m))
        return this.$t("chat.replyBranch");
      if (/(ສະກຸນ|ເງິນ|LAK|USD|THB|ບາດ|ໂດລາ|สกุล|เงิน|货币|currency)/.test(m))
        return this.$t("chat.replyCurrency");
      return this.$t("chat.replyDefault");
    },
    async sendChat(text) {
      const msg = (text || "").trim();
      if (!msg) return;
      // during questionnaire: text steps capture input, option steps ignore free typing
      if (this.survey.active) {
        const s = this.surveySteps[this.survey.step];
        if (s && s.type === "text") {
          this.addMsg(msg, "user");
          this.input = "";
          this.survey.answers[s.key] = msg;
          this.survey.step++;
          this.renderSurveyStep();
        }
        return;
      }
      if (
        /ເປີດຮ້ານ|ສອບຖາມ|questionnaire|start a new store|เริ่มเปิดร้าน|开新店/i.test(
          msg,
        )
      ) {
        this.addMsg(msg, "user");
        this.input = "";
        this.startSurvey();
        return;
      }
      this.addMsg(msg, "user");
      this.input = "";
      this.typing = true;
      this.scrollBottom();
      const answer = await new Promise((r) =>
        setTimeout(() => r(this.demoReply(msg)), 700),
      );
      this.typing = false;
      this.addMsg(answer, "bot");
    },
    onQuick(q) {
      this.sendChat(q);
    },
    // ===== questionnaire =====
    startSurvey() {
      this.survey.active = true;
      this.survey.step = 0;
      this.survey.answers = {};
      this.survey.multiSel = [];
      this.addMsg(this.$t("chat.surveyStart"), "bot");
      this.renderSurveyStep();
    },
    renderSurveyStep() {
      const s = this.surveySteps[this.survey.step];
      if (!s) {
        this.finishSurvey();
        return;
      }
      if (s.note) this.addMsg(this.$t(s.note), "bot");
      this.addMsg(this.$t(s.q), "bot");
      if (s.type === "text") {
        this.inputPlaceholder = "chat.placeholderText";
        this.$nextTick(() => {
          if (this.$refs.chatInputRef) this.$refs.chatInputRef.focus();
        });
        return;
      }
      this.inputPlaceholder = "chat.placeholderOptions";
      this.messages.push({ type: "options", step: this.survey.step });
      this.scrollBottom();
    },
    pickSingle(step, opt) {
      if (this.survey.step !== step) return;
      this.survey.answers[this.surveySteps[step].key] = this.$t(opt);
      this.survey.step++;
      this.renderSurveyStep();
    },
    toggleMulti(step, opt) {
      if (this.survey.step !== step) return;
      const i = this.survey.multiSel.indexOf(this.$t(opt));
      if (i >= 0) this.survey.multiSel.splice(i, 1);
      else this.survey.multiSel.push(this.$t(opt));
    },
    nextMulti(step) {
      if (this.survey.step !== step) return;
      this.survey.answers[this.surveySteps[step].key] = this.survey.multiSel
        .length
        ? this.survey.multiSel.join(", ")
        : "—";
      this.survey.multiSel = [];
      this.survey.step++;
      this.renderSurveyStep();
    },
    finishSurvey() {
      this.survey.active = false;
      this.inputPlaceholder = "chat.placeholder";
      const pcStep = this.surveySteps[1];
      const pcIdx = pcStep.options.findIndex(
        (o) => this.survey.answers.productCount === this.$t(o),
      );
      const pkg =
        pcIdx >= 0 ? this.$t(this.pkgKeys[pcIdx]) : this.$t("chat.pkgFallback");
      let sum = this.$t("chat.summaryTitle") + "\n\n";
      this.surveySteps.forEach((s) => {
        if (this.survey.answers[s.key]) {
          sum += `• ${this.$t(this.surveyLabels[s.key])}: ${
            this.survey.answers[s.key]
          }\n`;
        }
      });
      sum += `\n• ${this.$t("chat.summaryPkg")} ${pkg}`;
      this.addMsg(sum, "bot");
      this.messages.push({
        type: "actions",
        waHref:
          "https://wa.me/8562028492947?text=" +
          encodeURIComponent("ແບບສອບຖາມລູກຄ້າໃໝ່ PosSmart\n" + sum),
      });
      this.scrollBottom();
    },
  },
};
</script>

<style scoped>
.ps-chat {
  position: fixed;
  bottom: 96px;
  right: 20px;
  z-index: 60;
  width: min(380px, calc(100vw - 2.5rem));
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  box-shadow: var(--ps-shadow-hardlg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ps-chat__head {
  background: var(--ps-brand);
  color: #fff;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.ps-chat__avatar {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  padding: 4px;
}

.ps-chat__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.ps-chat__dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  background: var(--ps-lime);
  border: 1px solid #141021;
  border-radius: 50%;
}

.ps-chat__head-text {
  flex: 1;
  min-width: 0;
}

.ps-chat__title {
  font-weight: 900;
  line-height: 1.3;
  margin: 0;
}

.ps-chat__status {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 700;
  margin: 0;
}

.ps-chat__close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}
.ps-chat__close:hover {
  background: rgba(255, 255, 255, 0.15);
}

.ps-chat__msgs {
  flex: 1;
  overflow-y: auto;
  background: var(--ps-cream);
  padding: 16px;
  height: 340px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ps-chat__bubble {
  max-width: 85%;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: pre-line;
  border-radius: 16px;
}
.ps-chat__bubble--user {
  margin-left: auto;
  background: var(--ps-brand);
  color: #fff;
  border: 1px solid #141021;
  border-top-right-radius: 4px;
}
.ps-chat__bubble--bot {
  background: #fff;
  border: 1px solid #141021;
  border-top-left-radius: 4px;
  color: var(--ps-slate-700);
}

.ps-chat__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ps-chat__chip {
  font-size: 12px;
  font-weight: 900;
  color: var(--ps-brand);
  background: var(--ps-brand-soft);
  border: 1px solid #141021;
  border-radius: 999px;
  padding: 6px 12px;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.ps-chat__chip:hover {
  background: var(--ps-brand);
  color: #fff;
}
.ps-chat__chip--active {
  background: var(--ps-brand);
  color: #fff;
}
.ps-chat__chip--solid {
  background: var(--ps-brand);
  color: #fff;
}
.ps-chat__chip--solid:hover {
  background: var(--ps-brand-dark);
}
.ps-chat__chip--whatsapp {
  background: #10b981;
  color: #fff;
  text-decoration: none;
  padding: 8px 12px;
}
.ps-chat__chip--whatsapp:hover {
  background: #059669;
}

.ps-chat__typing {
  display: inline-flex;
  gap: 5px;
  padding: 12px 16px;
}
.ps-chat__typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ps-slate-400);
  animation: ps-bounce 1.2s infinite;
}
.ps-chat__typing span:nth-child(2) {
  animation-delay: 0.15s;
}
.ps-chat__typing span:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes ps-bounce {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.ps-chat__input-bar {
  border-top: 1px solid var(--ps-slate-300);
  background: #fff;
  padding: 12px;
}

.ps-chat__form {
  display: flex;
  gap: 8px;
}

.ps-chat__input {
  flex: 1;
  min-width: 0;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #141021;
  background: var(--ps-cream);
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--ps-ink);
}
.ps-chat__input:focus {
  outline: none;
  background: #fff;
}
.ps-chat__input::placeholder {
  color: var(--ps-slate-400);
}

.ps-chat__send {
  padding: 10px 16px;
  border-radius: 12px;
  background: var(--ps-brand);
  color: #fff;
  font-weight: 900;
  font-size: 0.875rem;
  border: 1px solid #141021;
  flex-shrink: 0;
  transition: background-color 0.15s ease;
}
.ps-chat__send:hover {
  background: var(--ps-brand-dark);
}

/* FAB */
.ps-chat__fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 60;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: var(--ps-brand);
  color: #fff;
  border: 1px solid #141021;
  box-shadow: var(--ps-shadow-hard);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}
.ps-chat__fab:hover {
  background: var(--ps-brand-dark);
}
.ps-chat__fab i.theme--light,
.ps-chat__close i.theme--light {
  color: #fff;
}
.ps-chat__fab-dot {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 14px;
  height: 14px;
  background: var(--ps-lime);
  border: 1px solid #141021;
  border-radius: 50%;
  animation: ps-pulse 1.5s infinite;
}
@keyframes ps-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@media (max-width: 480px) {
  .ps-chat {
    right: 12px;
    bottom: 88px;
  }
  .ps-chat__fab {
    right: 12px;
  }
}
</style>
